<template>
  <parent>
    <validation-observer
      ref="jobTemplateObserver"
      v-slot="{ invalid }"
      tag="form"
      class="d-flex flex-column justify-content-between position-relative position-sm-abs"
      style="top:0;bottom:0;left:0;right:0;"
    >
      <b-row class="flex-fill overflow-hidden">
        <b-col
          lg="3"
          sm="9"
          cols="12"
          class="h-auto h-sm-100"
        >
          <job-sidebar
            :general-tab="generalTab"
            :output-tabs="outputTabs"
            :input-tabs="inputTabs"
            :active-tab="activeTab"
            :active-child-index="activeChildIndex"
            @addTab="addTab"
            @selected="tabSelected"
            @childSelected="childTabSelected"
          />
        </b-col>
        <b-col
          lg="9"
          cols="12"
          class="h-auto h-sm-100"
        >
          <div
            class="mh-auto mh-sm-100"
            style="
            overflow-y:auto;
            overflow-x:hidden;
            "
          >
            <general-settings
              v-show="activeTab.id === generalTab.id"
              :job-template="jobTemplate"
              :from-live-jobs="fromLiveJobs"
              @liveJobChange="changeLiveJobStatus"
              @openInfo="openInfo"
            />
            <template v-for="tab in tabs">
              <div
                v-show="activeTab.id === tab.id"
                :key="tab.id"
              >
                <output-settings
                  v-if="isOutput(tab)"
                  ref="outputSettings"
                  :tab="tab"
                  :active-tab="{
                    childTabIndex: activeChildIndex,
                    tab: activeTab,
                  }"
                  :active="activeTab.id"
                  :edit="edit"
                  :is-live-job="isLiveJobTemplate"
                  :editing-output-index="activeChildIndex"
                  @customNameChange="
                    e => {
                      outputCustomNameChanged(e, tab.id)
                    }
                  "
                  @editingOutputIndex="initEditingOutput"
                  @remove="removeOutputTab(tab)"
                  @openInfo="openInfo"
                />
                <input-settings
                  v-else
                  :tab="tab"
                />
              </div>
            </template>
          </div>
        </b-col>
      </b-row>
      <div
        class="d-flex justify-content-end pb-2 pb-sm-0 pt-2 border-default border-t mt-1"
      >
        <b-button
          variant="bg-white"
          :to="{
            name: fromJobs
              ? 'jobs-create'
              : fromLiveJobs
                ? 'create-live-encoder'
                : 'job-templates',
            params: {
              backFromJobTemplate: true,
            },
          }"
        >
          Cancel
        </b-button>
        <button-loading
          variant="bg-primary"
          class="ml-1"
          :disabled="
            invalid ||
              isInvalidManualCheck ||
              outputGroupsAreInvalid ||
              submitLoading
          "
          :spinning="submitLoading"
          @click="createJobTemplate"
        >{{ !edit ? 'Create' : 'Save' }}
          <!-- <b-spinner
          v-if="submitLoading"
          class="position-absolute"
        /> -->
        </button-loading>
      </div>
      <b-modal
        v-model="outputGroupModal"
        title="Add output group"
        cancel-variant="bg-white"
        ok-title="Select"
        centered
        body-class="py-2 position-relative"
        :ok-disabled="!modalOutputGroupRadioSelect"
        @ok="outputGroupSelected"
      >
        <b-form-group
          v-slot="{ ariaDescribedby }"
          class="fileGroups"
          :class="fetchingGroupSettings ? 'blurForm' : ''"
        >
          <b-form-radio
            v-for="og in outputGroupOptions"
            :key="og.id"
            v-model="modalOutputGroupRadioSelect"
            :aria-describedby="ariaDescribedby"
            name="outputGroup-radio"
            :value="og"
            class="mb-2 align-items-start"
          >
            <div class="d-flex flex-column">
              <span>{{ og.name }}</span>
              <span class="text-sm text-secondary">{{ og.description }}</span>
            </div>
          </b-form-radio>
        </b-form-group>
        <div
          v-if="fetchingGroupSettings"
          class="d-flex position-absolute modalLoading justify-content-center align-items-center"
        >
          <b-spinner variant="primary" />
        </div>
      </b-modal>
      <info
        v-if="infoData"
        :info="infoData"
        @close="infoData = null"
      />
    </validation-observer>
  </parent>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { v4 as uuidv4 } from 'uuid'

import { required } from '@validations'

import { SidebarTabType } from '@/helpers/encoder/job-template-sidebar-enum'
import GeneralSettings from './settings/GeneralSettings.vue'
import JobSidebar from './job-sidebar/JobSidebar.vue'
import InputSettings from './settings/InputSettings.vue'
import OutputSettings from './settings/OutputSettings.vue'

const generalSettingsTab = {
  name: 'General Settings',
  id: '0',
  type: SidebarTabType.GENERAL,
}

export default {
  name: 'JobTemplateForm',
  components: {
    GeneralSettings,
    ValidationObserver,
    JobSidebar,
    InputSettings,
    OutputSettings,
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    fromJobs: {
      type: Boolean,
      default: false,
    },
    fromLiveJobs: {
      type: Boolean,
      default: false,
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      required,
      infoData: null,
      fetchingGroupSettings: false,
      outputGroupModal: false,
      modalOutputGroupRadioSelect: null,
      editingOutputIndex: null,
      generalTab: generalSettingsTab,
      activeTab: generalSettingsTab,
      activeChildIndex: null,
      isLiveJob: false,
      inputTabs: [
        {
          name: 'Input 1',
          id: '1',
          type: SidebarTabType.INPUT,
        },
      ],
      outputTabId: 0,
    }
  },
  computed: {
    tabs() {
      return [...this.inputTabs, ...this.outputTabs]
    },
    outputGroupOptions() {
      if (this.isLiveJobTemplate) {
        return this.$store.state.jobTemplates.outputGroupOptions.filter(el => el.name.toLowerCase().includes('hls'))
      }
      return this.$store.state.jobTemplates.outputGroupOptions
    },
    isInvalidManualCheck() {
      return this.outputTabs.length === 0
    },
    outputGroupsAreInvalid() {
      return !!this.outputTabs.find(og => {
        const isHLS = og.outputGroup.outputGroupsTypes.HLS === og.outputGroup.type || og.outputGroup.outputGroupsTypes.AppleSingleStreamHLS === og.outputGroup.type || og.outputGroup.outputGroupsTypes.AppleLLHLS === og.outputGroup.type

        if (og.outputGroup.outputs.length === 0) {
          return true
        }
        if (
          og.outputGroup.outputs.find(
            o => o.presetId === null || o.presetId === 0,
          )
        ) {
          return true
        }
        if (
          og.outputGroup.outputs.length > 1
          && og.outputGroup.outputs.some(o => !o.nameModifier)
        ) {
          return true
        }
        if (isHLS) {
          const folderNames = og.outputGroup.outputs.map(o => o.name)
          const folderNameDuplicate = folderNames.length !== [...new Set(folderNames)].length
          if (folderNameDuplicate) {
            const names = og.outputGroup.outputs.map(o => o.nameModifier)
            if (names.length !== [...new Set(names)].length) {
              return true
            }
            return false
          }
          return false
        }

        const names = og.outputGroup.outputs.map(o => o.nameModifier)
        if (names.length !== [...new Set(names)].length) {
          return true
        }

        return false
      })
    },
    isLiveJobTemplate: {
      get() {
        if (this.jobTemplate) {
          return !!this.jobTemplate.liveJobTemplate
        }
        return false
      },
      set(value) {
        this.jobTemplate.liveJobTemplate = value
      },
    },
    jobTemplate() {
      if (this.edit) {
        return this.$store.state.jobTemplates.jobTemplate
      }
      return this.$store.state.jobTemplates.emptyJobTemplate
    },
    outputTabs: {
      get() {
        return this.$store.state.jobTemplates.outputTabs
      },
      set(val) {
        this.$store.commit('jobTemplates/SET_OUTPUT_TABS', val)
      },
    },
  },
  watch: {
    // isLiveJobTemplate(val) {
    //   if (val) {
    //     this.tempOutputTabs = this.outputTabs
    //     this.outputTabs = this.outputTabs.filter(
    //       og => og.outputGroup.outputGroupsTypes.HLS === og.outputGroup.type,
    //     )
    //     return
    //   }
    //   this.outputTabs = this.tempOutputTabs
    // },
  },

  created() {
    const tab = this.$route.params.activeTab
    if (tab) {
      if (tab.childTabIndex !== null) {
        this.childTabSelected(tab)
      } else {
        this.tabSelected(tab.tab)
      }
    }
    this.init()
  },
  methods: {
    changeLiveJobStatus(e) {
      this.isLiveJobTemplate = e
    },
    initEditingOutput(i) {
      this.activeChildIndex = i
      this.editingOutputIndex = i
    },
    outputCustomNameChanged(newCustomName, tabId) {
      const index = this.outputTabs.findIndex(tab => tab.id === `${tabId}`)
      this.outputTabs[index].customName = newCustomName
    },
    init() {
      if (this.edit) {
        this.outputTabs = this.jobTemplate.groups.map(gr => this.createOutputTabObj(gr))
      }
    },
    isOutput(tab) {
      return tab.type === SidebarTabType.OUTPUT
    },
    addTab(type) {
      if (type === 'input') {
        this.inputTabs = [
          ...this.inputTabs,
          {
            name: `Input ${this.inputTabs.length + 1}`,
            id: uuidv4(),
            type: SidebarTabType.INPUT,
          },
        ]
      }
      if (type === 'output') {
        this.outputGroupModal = true
      }
    },
    async fetchGroupSettings(id) {
      return this.$store.dispatch('jobTemplates/fetchOutputGroupSettings', id)
    },
    async outputGroupSelected(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.fetchingGroupSettings = true
      this.fetchGroupSettings(this.modalOutputGroupRadioSelect.id)
        .then(res => {
          const tempObj = {
            name: this.modalOutputGroupRadioSelect.name,
            id: uuidv4(),
            type: SidebarTabType.OUTPUT,
            outputGroupId: this.modalOutputGroupRadioSelect.id,
            outputGroup: res.data,
            customName: res.data.customName,
            childTabs: res.data.outputs,
          }
          this.activeChildIndex = null
          this.outputTabs = [...this.outputTabs, tempObj]
          this.fetchingGroupSettings = false
          this.activeTab = tempObj
          this.outputGroupModal = false
        })
        .catch(() => {
          this.errorToast("An error occurred, couldn't fetch group settings.")
          this.outputGroupModal = false
        })
    },
    removeOutputTab(tab) {
      this.confirmDelete(
        'Remove output group',
        'Are you sure you want to remove this output group?',
        'Remove',
      ).then(ok => {
        if (ok) {
          this.outputTabs = this.outputTabs.filter(t => t.id !== tab.id)
          if (this.outputTabs.length) {
            this.activeTab = { ...this.outputTabs[0] }
          } else {
            this.activeTab = generalSettingsTab
          }
        }
      })
    },
    async createJobTemplate() {
      const isValid = await this.$refs.jobTemplateObserver.validate()
      if (isValid && !this.outputGroupsAreInvalid) {
        this.$emit('submit', {
          ...this.jobTemplate,
          groups: this.outputTabs.map(og => og.outputGroup),
        })
      }
    },
    createOutputTabObj(outputGroupElement) {
      return {
        name: outputGroupElement.name,
        id: `group${this.outputTabId++}`,
        type: SidebarTabType.OUTPUT,
        outputGroupId: outputGroupElement.id,
        outputGroup: outputGroupElement,
        customName: outputGroupElement.customName,
        childTabs: outputGroupElement.outputs,
      }
    },
    openInfo(e) {
      this.infoData = e
    },
    tabSelected(tab) {
      this.editingOutputIndex = null
      this.activeChildIndex = null
      this.activeTab = tab
    },
    childTabSelected(e) {
      this.editingOutputIndex = e.childTabIndex
      this.activeChildIndex = e.childTabIndex
      this.activeTab = e.tab
    },
  },
}
</script>
<style lang="scss" scoped>
.modalLoading {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
.blurForm {
  filter: blur(1px);
}
</style>
